import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IntegrationDisplayComponent } from '@principle-theorem/ng-principle-bridge-cloud';
import {
  ContentContainerComponent,
  CurrentPatientScope,
  OrganisationService,
  PractitionerSelectorComponent,
} from '@principle-theorem/ng-principle-shared';
import { NgDialogModule } from '@principle-theorem/ng-shared';
import {
  AppIntegrationFeature,
  IPatient,
  IPatientPrescriptionSummary,
  IPrescription,
  IStaffer,
  PrescriptionProvider,
  PrescriptionStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  Reffable,
  WithRef,
  filterUndefined,
  isSameRef,
  snapshot,
} from '@principle-theorem/shared';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { PatientPrescriptionFormComponent } from '../patient-prescription-form/patient-prescription-form.component';
import { PatientPrescriptionsListComponent } from '../patient-prescriptions-list/patient-prescriptions-list.component';
import {
  DeletePrescriptonData,
  PrescriptionService,
} from '../prescription.service';

export interface IPatientPrescriptionDialogData {
  patient: WithRef<IPatient>;
  prescriber: WithRef<IStaffer>;
  actionedBy: WithRef<IStaffer>;
}

@Component({
  selector: 'pr-patient-prescription-dialog',
  imports: [
    NgDialogModule,
    MatTooltipModule,
    PatientPrescriptionsListComponent,
    ContentContainerComponent,
    PatientPrescriptionFormComponent,
    PractitionerSelectorComponent,
    IntegrationDisplayComponent,
  ],
  templateUrl: './patient-prescription-dialog.component.html',
  styleUrl: './patient-prescription-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [PrescriptionService],
})
export class PatientPrescriptionDialogComponent implements OnDestroy {
  private _onDestroy$ = new Subject<void>();
  private _currentPatient = inject(CurrentPatientScope);
  readonly dialogRef = inject(MatDialogRef<PatientPrescriptionDialogComponent>);
  appIntegrationFeature = AppIntegrationFeature.CreatePrescription;
  prescription = inject(PrescriptionService);
  organisation = inject(OrganisationService);
  data = inject<IPatientPrescriptionDialogData>(MAT_DIALOG_DATA);
  prescribingAs = signal<WithRef<IStaffer> | undefined>(undefined);
  patient = toSignal(this._currentPatient.doc$, {
    initialValue: undefined,
  });

  feedbackMessage = computed(() => {
    if (!this.prescribingAs()) {
      return 'Select a prescriber to create a prescription.';
    }
    return '';
  });

  constructor() {
    this.prescription.loadAll$.next(PrescriptionStatus.Draft);

    this._getPrescribingAs$()
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((prescriber) => this.prescribingAs.set(prescriber));
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  async create(): Promise<void> {
    const prescriber = this.prescribingAs();
    const currentStaffer$ = this.organisation.staffer$.pipe(filterUndefined());
    const actionedBy = await snapshot(currentStaffer$);
    if (!prescriber) {
      return;
    }
    this.prescription.create(prescriber, actionedBy);
  }

  delete(data: DeletePrescriptonData): void {
    this.prescription.delete$.next(data);
    this.dialogRef.close();
  }

  async editPrescription(
    summary: Reffable<IPatientPrescriptionSummary>
  ): Promise<void> {
    if (summary.prescriptionProvider !== PrescriptionProvider.Principle) {
      return;
    }

    const prescription = (await Firestore.getDoc(
      summary.ref
    )) as unknown as WithRef<IPrescription>;

    this.prescription.selected$.next(prescription);
  }

  private _getPrescribingAs$(): Observable<WithRef<IStaffer> | undefined> {
    const staffer$ = this.organisation.staffer$.pipe(filterUndefined());
    const prescribers$ = this.organisation.prescribers$.pipe(filterUndefined());
    return combineLatest([staffer$, prescribers$]).pipe(
      map(([staffer, prescribers]) =>
        prescribers.find((prescriber) => isSameRef(staffer, prescriber))
      )
    );
  }
}
